import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bluetooth, RotateCw, Unlock } from "lucide-react";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import { fetchSockets, unlockDevice, fetchUnlockHistory } from "../services/device-access";
import { isBleUnlockSupported, unlockViaBlePin } from "../services/ble-access";
import "../styles/device-access.css";

const DeviceAccess = () => {
    const [sockets, setSockets] = useState([]);
    const [history, setHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [bleSupported] = useState(isBleUnlockSupported());

    const didMountRef = useRef(false);

    const getLocalISODate = useCallback((date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }, []);

    const getEffectiveRange = useCallback(() => {
        if (!dateFrom && !dateTo) {
            const today = getLocalISODate(new Date());
            return { dateFrom: today, dateTo: today, isDefaultToday: true };
        }

        // Якщо заповнили тільки одну дату — вважаємо це фільтром "за цей день"
        const effectiveFrom = dateFrom || dateTo;
        const effectiveTo = dateTo || dateFrom;
        return { dateFrom: effectiveFrom, dateTo: effectiveTo, isDefaultToday: false };
    }, [dateFrom, dateTo, getLocalISODate]);

    const fetchHistoryForRange = useCallback(async () => {
        const range = getEffectiveRange();
        return await fetchUnlockHistory({ dateFrom: range.dateFrom, dateTo: range.dateTo });
    }, [getEffectiveRange]);

    const refreshAll = useCallback(async () => {
        try {
            setLoading(true);
            const [socketsData, historyData] = await Promise.all([
                fetchSockets(),
                fetchHistoryForRange(),
            ]);
            setSockets(socketsData || []);
            setHistory(historyData || []);
            setFilteredHistory(historyData || []);
            setMessage("");
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchHistoryForRange]);

    const refreshHistory = useCallback(async () => {
        try {
            setLoading(true);
            const historyData = await fetchHistoryForRange();
            setHistory(historyData || []);
            setFilteredHistory(historyData || []);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchHistoryForRange]);

    useEffect(() => {
        refreshAll();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [refreshAll]);

    // Перезавантаження історії при зміні фільтра (серверна фільтрація)
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        refreshHistory();
    }, [refreshHistory]);

    const clearDateFilter = () => {
        setDateFrom("");
        setDateTo("");
    };

    const showHistoryFilters = isMobile ? showHistory : history.length > 0;
    const dateFilterActive = Boolean(dateFrom || dateTo);

    const renderToolbar = () => (
        <div className="config-header">
            <div className="header-buttons">
                <button
                    className="add-btn refresh toolbar-icon-btn"
                    type="button"
                    onClick={refreshAll}
                    disabled={loading}
                    title="Оновити"
                    aria-label="Оновити"
                >
                    <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                </button>
                {showHistoryFilters ? (
                    <FilterPopover
                        active={dateFilterActive}
                        title="Період історії"
                        hint="За замовчуванням показується історія за сьогодні."
                        buttonTitle="Фільтр за датою"
                        onClear={clearDateFilter}
                    >
                        <DateRangeFields
                            from={dateFrom}
                            to={dateTo}
                            onFromChange={setDateFrom}
                            onToChange={setDateTo}
                        />
                    </FilterPopover>
                ) : null}
            </div>
        </div>
    );

    const handleUnlock = async (socketId) => {
        try {
            setLoading(true);
            const result = await unlockDevice(socketId);
            if (result.success) {
                setMessage("✅ Розблоковано!");
                await refreshAll();
            } else {
                setMessage(`❌ Помилка: ${result.message || "Не вдалося розблокувати"}`);
            }
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleBleUnlock = async (socket) => {
        const pin = window.prompt(
            `Введіть BLE PIN для ${socket.location_name || socket.device_name || "дверей"}`
        );

        if (pin === null) {
            return;
        }

        try {
            setLoading(true);
            await unlockViaBlePin(pin);
            setMessage("✅ Двері відкрито через BLE");
        } catch (error) {
            setMessage(`❌ BLE помилка: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const renderMobileView = () => (
        <div className="device-access-container">
            {renderToolbar()}
            {message && <div className="message-box">{message}</div>}
            {!showHistory ? (
                <>
                    {!bleSupported && (
                        <p>BLE у цій збірці недоступний. Для офлайн-відкривання потрібен браузер із Web Bluetooth або native BLE plugin.</p>
                    )}
                    <div className="device-grid-mobile">
                        {sockets.map((socket) => (
                            <div key={socket.id} className="device-card-mobile">
                                <h4>{socket.location_name || ""}</h4>
                                <p className="device-card-mobile__subtitle">{socket.device_name || ""}</p>
                                <p className="device-card-mobile__status">{socket.online ? "Онлайн: можна відкрити віддалено" : "Офлайн: використайте BLE / код / картку"}</p>
                                <div className="device-card-mobile-actions">
                                    <button
                                        className="unlock-button-mobile"
                                        onClick={() => handleUnlock(socket.id)}
                                        disabled={loading || !socket.online}
                                    >
                                        <Unlock size={18} /> Відкрити
                                    </button>
                                    <button
                                        className="unlock-button-mobile unlock-button-mobile--secondary"
                                        onClick={() => handleBleUnlock(socket)}
                                        disabled={loading || !bleSupported}
                                    >
                                        <Bluetooth size={18} /> BLE + PIN
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        className="history-toggle"
                        onClick={() => setShowHistory(true)}
                    >
                        📋 Історія ({filteredHistory.length})
                    </button>
                </>
            ) : (
                <>
                    <h3 className="client-page-title keep-in-page-title">📋 Історія відкривань</h3>

                    <button
                        className="history-back-btn"
                        onClick={() => setShowHistory(false)}
                    >
                        ← Назад
                    </button>

                    <div className="history-list-mobile">
                        {filteredHistory.length === 0 ? (
                            <p className="no-records">Немає записів{(dateFrom || dateTo) ? " за вибраним періодом" : " за сьогодні"}</p>
                        ) : (
                            filteredHistory
                                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                .map((record) => (
                                    <div
                                        key={record.id}
                                        className={`history-item ${record.success ? "success" : "error"}`}
                                    >
                                        <div className="history-status">
                                            {record.success ? "✅" : "❌"}
                                        </div>
                                        <div className="history-info">
                                            <div className="history-socket">
                                                {(record.location_name || record.device_name)
                                                    ? `${record.location_name || ""}${record.location_name && record.device_name ? " / " : ""}${record.device_name || ""}`
                                                    : ""
                                                }
                                            </div>
                                            <div className="history-time">
                                                {new Date(record.created_at).toLocaleString("uk-UA")}
                                            </div>
                                            <div className="history-source">
                                                Джерело: {record.unlock_source}
                                            </div>
                                            {!record.success && record.error_message && (
                                                <div className="history-error">
                                                    Помилка: {record.error_message}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const renderDesktopView = () => (
        <div className="device-access-container">
            {renderToolbar()}
            {message && <div className="message-box">{message}</div>}
            {!bleSupported && (
                <p>BLE у цій збірці недоступний. Для офлайн-відкривання потрібен браузер із Web Bluetooth або native BLE plugin.</p>
            )}

            <div className="device-grid-desktop">
                {sockets.map((socket) => (
                    <div key={socket.id} className="device-card-desktop">
                        <h3>{socket.location_name || ""}</h3>
                        <p>{socket.device_name || ""}</p>
                        <p>{socket.online ? "Онлайн: можна відкрити віддалено" : "Офлайн: використайте BLE / код / картку"}</p>
                        <button
                            className="unlock-button"
                            onClick={() => handleUnlock(socket.id)}
                            disabled={loading || !socket.online}
                        >
                            <Unlock size={20} /> Відкрити замок
                        </button>
                        <button
                            className="unlock-button"
                            onClick={() => handleBleUnlock(socket)}
                            disabled={loading || !bleSupported}
                        >
                            <Bluetooth size={20} /> BLE + PIN
                        </button>
                    </div>
                ))}
            </div>

            {history.length > 0 && (
                <div className="history-section">
                    <div className="history-header">
                        <h3>Історія відкривань</h3>
                    </div>

                    {filteredHistory.length > 0 ? (
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Локація</th>
                                    <th>Девайс</th>
                                    <th>Час</th>
                                    <th>Джерело</th>
                                    <th>IP</th>
                                    <th>Примітки</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredHistory
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                    .map((record) => (
                                        <tr key={record.id} className={record.success ? "success" : "error"}>
                                            <td>{record.success ? "✅ Успіх" : "❌ Помилка"}</td>
                                            <td>{record.location_name || "-"}</td>
                                            <td>{record.device_name || "-"}</td>
                                            <td>{new Date(record.created_at).toLocaleString("uk-UA")}</td>
                                            <td>{record.unlock_source}</td>
                                            <td>{record.ip_address || "-"}</td>
                                            <td>{record.error_message || "-"}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="no-records">Немає записів{(dateFrom || dateTo) ? " за вибраним періодом" : " за сьогодні"}</p>
                    )}
                </div>
            )}
        </div>
    );

    return isMobile ? renderMobileView() : renderDesktopView();
};

export default DeviceAccess;
